import {AdvertiserCampaignInfoModel} from './advertiser-campaign-info.model';
import {State} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {AdvertiserDevicesService} from '../../services-advertiser/devices/devices.service';
import {Observable} from 'rxjs';
import {IResponse} from '@interfaces/response';
import {IAdvertiserDeviceCampaignInfo} from '@interfaces/advertiser/devices';
import {finalize, tap} from 'rxjs/operators';
import {ICampaignFullInfo} from '@interfaces/advertiser/campaign';
import {AdvertiserCampaignService} from '../../services-advertiser/campaign/campaign.service';
import {AdvertiserDevicesSortType} from '../../types/advertiser-devices.type';
import {OrderType} from '../../types/order.type';
import {NgxsDataRepository} from "@angular-ru/ngxs/repositories";
import {Payload, StateRepository} from "@angular-ru/ngxs/decorators";
import {DataActionWithoutSubscription} from "@shared/functions";

@StateRepository()
@State<AdvertiserCampaignInfoModel>({
  name: 'advertiserCampaignInfoState',
  defaults: {
    devices: [],
    itemsPerPage: 7,
    currentPage: 1,
    total: 0,
    startDate: '',
    endDate: '',
    bookedAmount: 0,
    spent: 0,
    progressDays: '',
    campaignName: '',
    devicesLoader: false,
    campaignInfoLoader: false,
    media: null,
    sort: 'id',
    order: 'DESC',
    isCampaignHasStat: false,
  }
})

@Injectable()
export class AdvertiserCampaignInfoState extends NgxsDataRepository<AdvertiserCampaignInfoModel> {
  constructor(private deviceService: AdvertiserDevicesService,
              private campaignService: AdvertiserCampaignService) {
    super();
  }

  @DataActionWithoutSubscription({cancelUncompleted: true})
  fetchDevices(@Payload('[Advertiser campaign info] get devices') id: number): Observable<IResponse<IAdvertiserDeviceCampaignInfo>> {
    const limit = this.ctx.getState().itemsPerPage;
    const skip = limit * (this.ctx.getState().currentPage - 1);
    const sort = this.ctx.getState().sort;
    const order = this.ctx.getState().order;

    this.ctx.patchState({devicesLoader: true});

    return this.deviceService.getCampaignDevices({limit, skip, campaignId: id, sort, order})
      .pipe(
        tap((devices) => {
          const state = this.ctx.getState();
          this.ctx.setState({...state, devices: devices.rows, total: devices.count, devicesLoader: false});
        }),
        finalize(() => this.ctx.patchState({devicesLoader: false}))
      );
  }

  @DataActionWithoutSubscription()
  getDevicesByPage(@Payload('[Advertiser campaign info] get devices by page number')
                     payload: { campaignId: number, page: number }): Observable<any> {
    this.ctx.patchState({currentPage: payload.page});
    return this.fetchDevices(payload.campaignId);
  }

  @DataActionWithoutSubscription()
  getCampaignData(@Payload('[Advertiser campaign info] get campaign by id') id: number): Observable<ICampaignFullInfo> {
    this.ctx.patchState({campaignInfoLoader: true});
    return this.campaignService.getAdvertiserCampaignById(id)
      .pipe(
        tap((campaign) => {
          const state = this.ctx.getState();
          this.ctx.setState({
            ...state,
            startDate: campaign.start, endDate: campaign.finish, media: campaign.media,
            campaignInfoLoader: false, bookedAmount: campaign.estimatedBudget, spent: campaign.spentBudget,
            progressDays: `${campaign.playedDays} / ${campaign.duration}`, campaignName: campaign.name,
            isCampaignHasStat: campaign.billingProcessed
          });
        }),
        finalize(() => this.ctx.patchState({campaignInfoLoader: false}))
      );
  }

  @DataActionWithoutSubscription()
  sortDevices(@Payload('[Advertiser campaign info] sort devices')
                payload: { sort: AdvertiserDevicesSortType, order: OrderType, campaignId: number }): Observable<any> {
    this.ctx.patchState({sort: payload.sort, order: payload.order});
    return this.fetchDevices(payload.campaignId);
  }
}
